import * as R from 'ramda'
import { useState } from 'react'
import { Box, IconButton, Stack, styled, Typography } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { BottomDrawer } from '$common/bottom-drawer'
import { BookmarkIcon, ArrowRightIcon } from '$common/icons'
import { SecondaryButton } from '$common/buttons'
import { t } from '$i18n'
import { showCreateBookmarkCollectionModal } from '$comp/modals/create-bookmark-collection-modal'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { addBookmark, deleteBookmark, getCollections } from '$logic/bookmark'
import { showBookmarkSelectionListModal } from '$comp/modals/bookmark-selection-list-modal/bookmark-selection-list-modal.api'
import { nothing } from '$func/utils'
import { Loading } from '$common/loading/chat-loading'
import { BookmarkListItem } from './bookmark-list-item'
import { PrimaryOutlinedButton } from '$common/buttons/primary-outlined-button'

const Root = styled(Stack)`
  padding: 20px 10px 5px;
`

const LoadingBox = styled(Stack)`
  position: absolute;
  top: calc(50% - 20px);
`

interface Props {
  oldCollections: string[]
  bookmarkData: { contentUri: string; cover: string }
  onDone?: (updated: boolean) => unknown
  isOpen?: boolean
}

export function BookmarkSelectionListModal({
  oldCollections,
  bookmarkData,
  onDone,
  isOpen,
}: Props) {
  const [selectedCollections, setSelectedCollections] = useState<
    Record<string, boolean>
  >(oldCollections.reduce((acc, col) => ({ ...acc, [col]: true }), {}))

  const { value: list, loading } = useAsyncFunction(getCollections, [])
  const onListItemClick = (title: string) =>
    setSelectedCollections(prevState => ({
      ...prevState,
      [title]: !prevState[title],
    }))

  const onSubmit = async () => {
    const titles = R.toPairs(selectedCollections)
      .filter(([_, selected]) => selected)
      .map(([title]) => title)
    const newAdded = R.difference(titles, oldCollections)
    const deleted = R.difference(oldCollections, titles)

    const { contentUri, cover } = bookmarkData

    async function handleAdd() {
      if (newAdded.length) await addBookmark(contentUri, cover, newAdded)
    }

    async function handleDelete() {
      if (deleted.length)
        await deleteBookmark(
          deleted.map(collectionTitle => ({ collectionTitle, contentUri })),
        )
    }

    await Promise.all([handleAdd(), handleDelete()])
    onDone?.(Boolean(deleted.length || newAdded.length))
    closeCurrentModal()
  }

  return (
    <BottomDrawer isOpen={isOpen} onClose={closeCurrentModal}>
      <Root>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
            marginBottom: '20px',
            alignItems: 'center',
            marginRight: '5px',
          }}
          gap="5px"
        >
          <IconButton
            onClick={closeCurrentModal}
            sx={{ width: '24px', height: '24px' }}
          >
            <ArrowRightIcon color="primary" />
          </IconButton>
          <Typography variant="subtitle1" color="primary">
            {t('bookmarkModal.bookmark')}
          </Typography>
        </Box>
        <Stack
          alignItems="center"
          gap="8px"
          height="216px"
          padding="20px 2px 0"
          overflow="auto"
        >
          {loading ? (
            <LoadingBox>
              <Loading color="secondary.main" />
            </LoadingBox>
          ) : list?.length ? (
            list.map(listItem => (
              <BookmarkListItem
                title={listItem.title}
                contentCount={listItem.markCount}
                cover={listItem.cover}
                selected={!!selectedCollections[listItem.title]}
                onClick={() => onListItemClick(listItem.title)}
              />
            ))
          ) : (
            <>
              <BookmarkIcon
                sx={{ width: '40px', height: '40px', color: 'lightGray' }}
              />
              <Typography variant="subtitle3" color="black">
                {t('bookmarkSelectionListModal.noList')}
              </Typography>
              <Typography variant="body2" color="gray">
                {t('bookmarkSelectionListModal.shouldMakeAList')}
              </Typography>
            </>
          )}
        </Stack>

        <Stack padding="20px" direction="row" width="100%" gap="12px">
          <PrimaryOutlinedButton
            text={t('bookmarkSelectionListModal.createCollection')}
            onClick={() => {
              closeCurrentModal()
              showCreateBookmarkCollectionModal({
                onDone: () =>
                  showBookmarkSelectionListModal({
                    oldCollections,
                    bookmarkData,
                  }),
              })
            }}
            fullWidth
          />

          <SecondaryButton
            text={t('common.ok')}
            onClick={onSubmit}
            fullWidth
            disabled={loading}
          />
        </Stack>
      </Root>
    </BottomDrawer>
  )
}

BookmarkSelectionListModal.defaultProps = {
  onDone: nothing,
  isOpen: false,
}
